
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Search, MessageCircle, Home, User, CreditCard, Shield, Bell } from 'lucide-react';
import JsonLd from '../components/SEO/JsonLd';
import Header from '../components/layout/Header';
import ImprovedUniversalHeader from '../components/ImprovedUniversalHeader';
import LandlordHeader from '../components/LandlordHeader';
import { useAuth } from '../contexts/AuthContext';
import { useRealTimeNotifications } from '../hooks/useRealTimeNotifications';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import MobileNotificationCenter from '../components/MobileNotificationCenter';

const HelpCenter = () => {
  const navigate = useNavigate();
  const { user, logout, profile } = useAuth();
  const { stats } = useRealTimeNotifications();
  const [showNotifications, setShowNotifications] = useState(false);

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Determine which header to render based on authentication state - exactly like the dashboard pages
  const renderHeader = () => {
    if (!user) {
      return <Header />;
    }
    
    // Check if user is landlord - same logic as LandlordDashboard.tsx
    if (profile?.user_type === 'landlord') {
      return <LandlordHeader user={user} onSignOut={handleSignOut} />;
    }
    
    // For renters - same setup as Dashboard.tsx
    const rightActions = (
      <Popover open={showNotifications} onOpenChange={setShowNotifications}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="relative p-2 hover:bg-slate-800/50 rounded-xl transition-colors text-white"
          >
            <Bell className="w-4 h-4" />
            {stats.unread > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
              >
                {stats.unread > 99 ? '99+' : stats.unread}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          align="end" 
          className="w-80 sm:w-96 p-0 bg-background border shadow-lg"
          sideOffset={8}
        >
          <MobileNotificationCenter className="border-0 shadow-none" />
        </PopoverContent>
      </Popover>
    );

    return (
      <ImprovedUniversalHeader 
        title="Help Center" 
        showBackButton={false} 
        rightActions={rightActions}
      />
    );
  };

  const helpCategories = [
    {
      icon: <Search className="w-8 h-8 text-pink-400" />,
      title: "Finding Properties",
      description: "Learn how to search, filter, and discover your perfect rental",
      link: "/help/finding-properties",
      articles: [
        "How to use AI-powered search",
        "Understanding match percentages",
        "Setting up search preferences",
        "Saving favorite properties"
      ]
    },
    {
      icon: <MessageCircle className="w-8 h-8 text-green-400" />,
      title: "Communication",
      description: "Connect with landlords and manage your conversations",
      link: "/help/communication",
      articles: [
        "Messaging landlords directly",
        "Using Fiona AI assistant",
        "Scheduling property tours",
        "Managing your inbox"
      ]
    },
    {
      icon: <Home className="w-8 h-8 text-blue-400" />,
      title: "For Landlords",
      description: "List properties and manage your rental business",
      link: "/help/landlords",
      articles: [
        "Creating property listings",
        "Managing tenant applications",
        "Understanding analytics",
        "Setting up tours"
      ]
    },
    {
      icon: <User className="w-8 h-8 text-purple-400" />,
      title: "Account Management",
      description: "Manage your profile and account settings",
      link: "/help/account",
      articles: [
        "Creating your profile",
        "Updating preferences",
        "Account security",
        "Notification settings"
      ]
    },
    {
      icon: <CreditCard className="w-8 h-8 text-orange-400" />,
      title: "Billing & Pricing",
      description: "Understand our pricing and manage payments",
      link: "/help/billing",
      articles: [
        "Free renter accounts",
        "Landlord subscription plans",
        "Payment methods",
        "Billing questions"
      ]
    },
    {
      icon: <Shield className="w-8 h-8 text-red-400" />,
      title: "Safety & Security",
      description: "Stay safe while renting with our platform",
      link: "/help/safety",
      articles: [
        "Verified listings policy",
        "Reporting suspicious activity",
        "Privacy protection",
        "Safe meeting practices"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {renderHeader()}
      
      {/* Page Title Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-white mb-4">
            Help Center
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Find answers to common questions and learn how to make the most of your Flamingo experience
          </p>
        </div>

        {/* JSON-LD: FAQPage */}
        <JsonLd data={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'How does Flamingo match me with rentals?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Flamingo learns your preferences and ranks listings by match % using verified data.'
              }
            },
            {
              '@type': 'Question',
              name: 'Is messaging secure?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. We use secure Supabase-backed messaging with read indicators and RLS policies.'
              }
            },
            {
              '@type': 'Question',
              name: 'How do landlords list properties?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Use Add Listing to publish a verified listing with photos, amenities, and availability.'
              }
            }
          ]
        }} />
        
        {/* Help Categories */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {helpCategories.map((category, index) => (
            <Card 
              key={index} 
              className="p-6 bg-slate-800/50 border-slate-700 backdrop-blur-sm hover:bg-slate-800/70 hover:border-pink-500/50 transition-all duration-300 cursor-pointer"
              onClick={() => navigate(category.link)}
            >
              <div className="mb-4">{category.icon}</div>
              <h3 className="text-xl font-bold text-white mb-3">
                {category.title}
              </h3>
              <p className="text-slate-300 mb-4">
                {category.description}
              </p>
              <ul className="space-y-2">
                {category.articles.map((article, articleIndex) => (
                  <li key={articleIndex} className="text-sm text-slate-400">
                    • {article}
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>

        {/* Contact Support */}
        <div className="mt-16 text-center">
          <Card className="p-8 max-w-2xl mx-auto bg-slate-800/50 border-slate-700 backdrop-blur-sm">
            <h2 className="text-2xl font-bold text-white mb-4">
              Still Need Help?
            </h2>
            <p className="text-slate-300 mb-6">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <Button 
              onClick={() => navigate('/contact-us')}
              className="bg-pink-500 hover:bg-pink-600 text-white"
            >
              Contact Support
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
